import { QuestionBase } from '../../base/question.base';
import { times, difference, uniq, sortBy } from 'lodash';
import { WeeklyTableHandlerInterface } from './weekly-table-handler.interface';

export class WeeklyTableHandler implements WeeklyTableHandlerInterface {
  // set zeropoint (format yyyy-MM-dd)
  private _columns = [];
  get colZeroPoint(): string {
    return this.question.get(['weeklyTable', 'colZeroPoint']);
  }
  get colIds(): number[] {
    return this.question.get(['weeklyTable', 'colIds']) || [];
  }
  get columns(): { colId: number; alphabet: string; header: Date }[] {
    return this.colIds.map(colId => {
      return {
        colId,
        alphabet: this.turnColIdIntoAlphabet(colId),
        header: this.turnColIdIntoHeader(colId)
      };
    });
  }
  get rowIds(): string[] {
    return this.question.get(['weeklyTable', 'rowIds']) || [];
  }
  set rowIds(v: string[]) {
    this.question.set(['weeklyTable', 'rowIds'], v);
  }

  get dateRange(): [number, number] {
    return this.question.get(['weeklyTable', 'dateRange']);
  }
  set dateRange(v: [number, number]) {
    this.question.set(['weeklyTable', 'dateRange'], v);
  }

  constructor(private question: QuestionBase) {
    if (!this.colZeroPoint) this.setColZeroPoint();
    if (!this.dateRange) this.setDateRange();
  }
  resetColumns(dateRange: [Date, Date]): void {
    let start_n = this.getDaysBtwnAnB(
        this.turnUnixtimeToYMD(dateRange[0].getTime()),
        this.colZeroPoint
      ),
      end_n = this.getDaysBtwnAnB(
        this.turnUnixtimeToYMD(dateRange[1].getTime()),
        this.colZeroPoint
      );
    let distance_n = end_n - start_n;
    let colIds_n = times(distance_n + 1, i => start_n + i);

    // get current colIds without GAPs!
    let start_o = Math.min(...this.colIds),
      end_o = Math.max(...this.colIds);
    let distance_o = end_o - start_o;
    let colIds_o_without_gaps = times(distance_o + 1, i => start_o + i);

    // add and remove accordingly
    let colIds_merged = this.colIds;
    difference(colIds_n, colIds_o_without_gaps).forEach(colId =>
      colIds_merged.push(colId)
    );
    difference(colIds_o_without_gaps, colIds_n).forEach(colId =>
      colIds_merged.splice(colIds_merged.indexOf(colId), 1)
    );
    colIds_merged = sortBy(uniq(colIds_merged), colId => colId);

    // set merged result.
    this.question.set(['weeklyTable', 'colIds'], colIds_merged);
    this.reFreshColumns();
  }
  resetRows(y: number) {
    let rowIds = this.rowIds;
    let y_without_header_row = y - 1;
    let diff = y_without_header_row - rowIds.length;
    if (diff > 0) {
      times(diff, i => rowIds.push('-'));
    } else if (diff < 0) {
      times(diff, i => rowIds.pop());
    }
    this.question.set(['weeklyTable', 'rowIds'], rowIds);
  }

  deleteCol(colId: number) {
    let index = this.colIds.findIndex(n => n === colId);
    let colIds = this.colIds;
    // let columnHeaders = this.columnHeaders;

    colIds.splice(index, 1);
    // columnHeaders.splice(index, 1);

    this.question.set(['weeklyTable', 'colIds'], colIds);
    // this.question.set(['weeklyTable', 'columnHeaders'], columnHeaders);
  }
  isColInsertable(colId: number): boolean {
    if (Math.max(...this.colIds) <= colId) return false;
    if (Math.min(...this.colIds) >= colId) return false;
    return !this.colIds.includes(colId);
  }
  insertCol(colId: number, columnHeader: string = '') {
    if (this.isColInsertable(colId)) {
      let index = this.colIds.findIndex(n => n > colId);

      let colIds = this.colIds;
      // let columnHeaders = this.columnHeaders;

      colIds.splice(index, 0, colId);
      // columnHeaders.splice(index, 0, columnHeader);

      this.question.set(['weeklyTable', 'colIds'], colIds);
      // this.question.set(['weeklyTable', 'columnHeaders'], columnHeaders);
    }
  }
  deleteRow(rowIndex: number) {
    let rowIds = this.rowIds;

    rowIds.splice(rowIndex, 1);

    this.question.set(['weeklyTable', 'rowIds'], rowIds);
    this.reFreshColumns();
  }
  insertRow(rowIndex: number) {
    if (rowIndex < 0) rowIndex = 0;

    let rowIds = this.rowIds;

    rowIds.splice(rowIndex, 0, '-');

    this.question.set(['weeklyTable', 'rowIds'], rowIds);
    this.reFreshColumns();
  }
  private reFreshColumns() {
    this._columns = this.colIds.map(colId => {
      return {
        colId,
        alphabet: this.turnColIdIntoAlphabet(colId),
        header: this.turnColIdIntoHeader(colId)
      };
    });
  }
  private setColZeroPoint() {
    let dateObj = new Date(Date.now());
    let month = dateObj.getUTCMonth() + 1; //months from 1-12
    let day = dateObj.getUTCDate();
    let year = dateObj.getUTCFullYear();
    this.question.set(
      ['weeklyTable', 'colZeroPoint'],
      `${year}-${('0' + month).slice(-2)}-${('0' + day).slice(-2)}`
    );
  }
  private setDateRange() {
    this.dateRange = [Date.now(), Date.now() + 7 * 24 * 60 * 60 * 1000];
  }
  private getDaysBtwnAnB(A: string, B: string): number {
    if (
      !A.match(/^[\d]{4}-[\d]{2}-[\d]{2}$/) ||
      !B.match(/^[\d]{4}-[\d]{2}-[\d]{2}$/)
    )
      throw new Error('unsupported param type: ' + A + ' or ' + B);
    let to = new Date(A).getTime(),
      from = new Date(B).getTime();
    return Math.round((to - from) / (1000 * 60 * 60 * 24));
  }
  private turnUnixtimeToYMD(ms: number): string {
    let dateObj = new Date(ms);
    let month = dateObj.getUTCMonth() + 1; //months from 1-12
    let day = dateObj.getUTCDate();
    let year = dateObj.getUTCFullYear();
    return `${year}-${('0' + month).slice(-2)}-${('0' + day).slice(-2)}`;
  }
  private turnColIdIntoAlphabet(colId: number) {
    let minColId = Math.min(...this.colIds);
    let n = colId - minColId + 1;
    if (n > 255) throw new Error('table columns can go over 255');
    let a = 'A'.charCodeAt(0);
    let answer = Math.floor(n / 26);
    let remainder = Math.floor(n % 26);
    return (
      (answer !== 0 ? String.fromCharCode(a + answer) : '') +
      String.fromCharCode(a + remainder)
    );
  }
  private turnColIdIntoHeader(colId: number): Date {
    return new Date(
      new Date(this.colZeroPoint).getTime() + colId * 24 * 60 * 60 * 1000
    );
  }
}
